import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { DOC_STATUS, fmtDate, authUrl } from "../../lib/re";
import { Badge, Field, Btn, Modal, Spinner, DateInput } from "./ui";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Plus, Eye, Upload, Trash2, ShieldCheck, History } from "lucide-react";

const EMPTY = { category: "", name: "", owner_id: "", issue_date: "", expiry_date: "", status: "", notes: "" };

function DocForm({ property, categories, onClose, onSaved, preset }) {
  const { user } = useAuth();
  const [f, setF] = useState({ ...EMPTY, category: preset || categories[0] || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const canValidate = ["admin", "diretor", "assistente"].includes(user.role);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try { await api.post(`/re/properties/${property.id}/documents`, fd); toast.success("Documento registado"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro ao carregar o documento"); }
    setBusy(false);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Adicionar documento" onClose={onClose} testId="doc-form-modal">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Categoria *" span={2}><select className="input-almar" value={f.category} onChange={set("category")} data-testid="doc-form-category">{categories.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Nome do documento" span={2}><input className="input-almar" value={f.name} onChange={set("name")} placeholder="Opcional" data-testid="doc-form-name" /></Field>
        <Field label="Proprietário associado" span={2}>
          <select className="input-almar" value={f.owner_id} onChange={set("owner_id")} data-testid="doc-form-owner"><option value="">—</option>{(property.owners || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
        </Field>
        <Field label="Data de emissão"><DateInput value={f.issue_date} onChange={(v) => setF((x) => ({ ...x, issue_date: v }))} testId="doc-form-issue" /></Field>
        <Field label="Data de validade"><DateInput value={f.expiry_date} onChange={(v) => setF((x) => ({ ...x, expiry_date: v }))} testId="doc-form-expiry" /></Field>
        <Field label="Estado" span={2}>
          <select className="input-almar" value={f.status} onChange={set("status")} data-testid="doc-form-status">
            <option value="">Automático (por verificar / em falta)</option>
            {Object.entries(DOC_STATUS).filter(([k]) => k !== "validado" || canValidate).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Notas internas" span={2}><textarea rows={2} className="input-almar" value={f.notes} onChange={set("notes")} data-testid="doc-form-notes" /></Field>
        <Field label="Ficheiro (PDF, JPG, PNG...)" span={2}><input type="file" className="text-sm text-slate-300" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-form-file" /></Field>
        <p className="sm:col-span-2 text-[11px] text-slate-500">O carregamento não torna o documento juridicamente válido. A validação é feita por um utilizador autorizado.</p>
        <div className="sm:col-span-2"><Btn type="submit" disabled={busy} className="w-full" data-testid="doc-form-submit">{busy ? "A carregar..." : "Guardar documento"}</Btn></div>
      </form>
    </Modal>
  );
}

function DocRow({ d, canValidate, onAct, onPreview }) {
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  return (
    <div className="card-almar p-4" data-testid={`property-doc-${d.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-100 font-medium truncate">{d.name}</p>
          <p className="text-xs text-slate-500">{d.category} · Carregado {fmtDate(d.created_at)} por {d.uploaded_by?.name}{d.expiry_date ? ` · Validade ${fmtDate(d.expiry_date)}` : ""}{d.validated_by ? ` · Validado por ${d.validated_by.name}` : ""}</p>
        </div>
        <Badge value={d.status} label={DOC_STATUS[d.status]} testId={`doc-status-${d.id}`} />
        <div className="flex items-center gap-1">
          {d.has_file && <button title="Pré-visualizar" onClick={() => onPreview(d)} data-testid={`doc-preview-${d.id}`} className="p-2 text-slate-400 hover:text-[#C9A84C]"><Eye size={16} /></button>}
          {canValidate && d.has_file && d.status !== "validado" && <button title="Validar" onClick={() => onAct("patch", d, { status: "validado" })} data-testid={`doc-validate-${d.id}`} className="p-2 text-slate-400 hover:text-emerald-400"><ShieldCheck size={16} /></button>}
          <button title="Carregar/substituir ficheiro" onClick={() => fileRef.current?.click()} data-testid={`doc-upload-${d.id}`} className="p-2 text-slate-400 hover:text-sky-300"><Upload size={16} /></button>
          <button title="Histórico" onClick={() => setOpen(!open)} data-testid={`doc-history-${d.id}`} className="p-2 text-slate-400 hover:text-slate-100"><History size={16} /></button>
          <button title="Eliminar" onClick={() => window.confirm("Eliminar documento?") && onAct("delete", d)} data-testid={`doc-delete-${d.id}`} className="p-2 text-slate-400 hover:text-red-400"><Trash2 size={16} /></button>
          <input ref={fileRef} type="file" hidden onChange={(e) => { onAct("file", d, e.target.files[0]); e.target.value = ""; }} />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 items-center">
        <select className="input-almar py-1 text-xs w-auto" value={d.status} onChange={(e) => onAct("patch", d, { status: e.target.value })} data-testid={`doc-status-select-${d.id}`}>
          {Object.entries(DOC_STATUS).filter(([k]) => k !== "validado" || canValidate || d.status === "validado").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {d.notes && <span className="text-xs text-slate-400">{d.notes}</span>}
      </div>
      {open && (
        <div className="mt-3 border-t border-[#1E3A56] pt-3 space-y-1">
          {(d.history || []).slice().reverse().map((h, i) => <p key={i} className="text-xs text-slate-400">{fmtDate(h.at)} {h.at?.slice(11, 16)} · {h.user} · {h.action}</p>)}
        </div>
      )}
    </div>
  );
}

export function PropertyDocs({ property, onChange }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const canValidate = ["admin", "diretor", "assistente"].includes(user.role);

  const load = useCallback(() => api.get(`/re/properties/${property.id}/documents`).then((r) => setData(r.data)).catch(() => setData({ items: [], summary: { checklist: [] } })), [property.id]);
  useEffect(() => { load(); api.get("/re/settings").then((r) => setCategories(r.data.doc_categories)).catch(() => {}); }, [load]);

  const refresh = () => { load(); onChange(); };
  const onAct = async (kind, d, arg) => {
    try {
      if (kind === "patch") await api.patch(`/re/documents/${d.id}`, arg);
      if (kind === "delete") await api.delete(`/re/documents/${d.id}`);
      if (kind === "file") { if (!arg) return; const fd = new FormData(); fd.append("file", arg); await api.post(`/re/documents/${d.id}/file`, fd); }
      toast.success("Documento atualizado"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar o documento"); }
  };

  if (!data) return <Spinner />;
  const s = data.summary;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="card-almar p-4 h-fit" data-testid="doc-checklist">
        <h4 className="text-sm font-semibold text-slate-100 mb-1">Checklist documental</h4>
        <p className="text-xs text-slate-500 mb-3">{s.complete ? "Documentação completa" : `${s.missing.length} em falta${s.expired ? ` · ${s.expired} expirado(s)` : ""}`}</p>
        {s.checklist.map((c) => (
          <button key={c.category} onClick={() => !c.ok && setForm(c.category)} data-testid={`checklist-item-${c.category}`}
            className="w-full flex items-center gap-2 py-1.5 text-left text-sm">
            {c.ok ? <CheckCircle2 size={15} className="text-emerald-400 flex-shrink-0" /> : <AlertTriangle size={15} className="text-amber-300 flex-shrink-0" />}
            <span className={c.ok ? "text-slate-300" : "text-amber-200 hover:underline"}>{c.category}{c.need ? ` (${Math.min(c.have, c.need)}/${c.need})` : ""}</span>
          </button>
        ))}
        {s.expiring > 0 && <p className="text-xs text-amber-300 mt-2">{s.expiring} documento(s) expira(m) nos próximos 30 dias</p>}
      </div>
      <div className="lg:col-span-2 space-y-3">
        <div className="flex justify-end"><Btn onClick={() => setForm(categories[0])} data-testid="add-doc-btn"><Plus size={15} /> Adicionar documento</Btn></div>
        {data.items.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Sem documentos carregados.</p>}
        {data.items.map((d) => <DocRow key={d.id} d={d} canValidate={canValidate} onAct={onAct} onPreview={setPreview} />)}
      </div>
      {form && categories.length > 0 && <DocForm property={property} categories={categories} preset={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); refresh(); }} />}
      {preview && <DocPreview d={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

export function DocPreview({ d, onClose }) {
  const url = authUrl(`/re/documents/${d.id}/download`);
  const isImg = (d.content_type || "").startsWith("image");
  const isPdf = (d.content_type || "").includes("pdf");
  return (
    <Modal title={d.name} onClose={onClose} wide testId="doc-preview-modal">
      <div className="h-[70vh] bg-[#0A1622] rounded-lg flex items-center justify-center overflow-hidden">
        {isImg ? <img src={url} alt={d.name} className="max-h-full max-w-full object-contain" /> : isPdf ? <iframe title={d.name} src={url} className="w-full h-full" /> : <a href={url} target="_blank" rel="noreferrer" className="text-[#E0C574] underline">Descarregar ficheiro</a>}
      </div>
    </Modal>
  );
}
